import styled, { css } from "styled-components";

import theme from "../../styles/theme";

export const PageWrap = styled.div`
  max-width: ${theme.breakpointsCustom.max}px;
  margin: 0 auto;
  padding: 0 12px 96px;
  font-family: "Montserrat", sans-serif;
  color: ${theme.colors.white};
`;

export const HeroSection = styled.section`
  padding: 40px 0 8px;
  display: flex;
  flex-wrap: wrap;
  align-items: flex-end;
  justify-content: space-between;
  gap: 20px;
`;

export const HeroText = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
`;

export const HeroTitle = styled.h1`
  font-size: 44px;
  line-height: 1.05;
  font-weight: 800;
  margin: 0;
  letter-spacing: -1px;

  @media (max-width: ${theme.breakpointsCustom.sm}px) {
    font-size: 32px;
  }
`;

export const Lead = styled.p`
  margin: 0;
  max-width: 620px;
  font-size: 15px;
  line-height: 1.6;
  color: ${theme.colors.grey};
`;

export const HeroStats = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
`;

export const StatBox = styled.div`
  background: ${theme.colors.darkLight};
  border: 2px solid ${theme.colors.secondaryDark};
  padding: 12px 16px;
  min-width: 104px;
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

export const StatValue = styled.div`
  font-family: "Roboto Condensed", sans-serif;
  font-size: 26px;
  font-weight: 700;
  color: ${theme.colors.green};
  line-height: 1;
`;

export const StatLabel = styled.div`
  font-size: 9px;
  font-weight: 700;
  letter-spacing: 1px;
  text-transform: uppercase;
  color: ${theme.colors.grey};
`;

export const FilterBar = styled.section`
  position: sticky;
  top: 60px;
  z-index: 40;
  backdrop-filter: blur(8px);
  padding: 16px 0;
  margin-top: 16px;
  border-bottom: 2px solid ${theme.colors.secondaryDark};
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
  align-items: center;
`;

export const FilterInput = styled.input`
  background: ${theme.colors.darkLight};
  border: 2px solid ${theme.colors.secondaryDark};
  color: ${theme.colors.white};
  font-family: "Montserrat", sans-serif;
  padding: 8px 12px;
  font-size: 13px;
  outline: none;
  width: 200px;

  &::placeholder {
    color: ${theme.colors.grey};
  }
`;

export const Spacer = styled.div`
  flex: 1;
`;

export const TabGroup = styled.div`
  display: flex;
  gap: 2px;
  background: ${theme.colors.secondaryDark};
  padding: 2px;
`;

export const TabButton = styled.button`
  background: ${({ $active }) => ($active ? theme.colors.dark : "transparent")};
  color: ${({ $active }) => ($active ? theme.colors.green : theme.colors.white)};
  border: none;
  border-bottom: 2.5px solid ${({ $active }) => ($active ? theme.colors.green : "transparent")};
  padding: 10px 18px;
  font-size: 14px;
  font-family: "Montserrat", sans-serif;
  font-weight: 700;
  cursor: pointer;
`;

export const SectionHeader = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  border-bottom: 3px solid ${theme.colors.darkLight};
  margin: ${({ $first }) => ($first ? "40px" : "48px")} 0 20px;
  padding-bottom: 10px;
`;

export const SectionTitle = styled.h2`
  font-size: 28px;
  font-weight: 700;
  margin: 0;
`;

// Sits beside a section title and says, in a phrase, what the section contains. It used to hold the
// Postgres view the data came from, where all-caps and wide tracking read as a tag; now that it
// carries a sentence, both would just shout.
export const SectionNote = styled.span`
  font-family: "Roboto Condensed", sans-serif;
  font-size: 13px;
  color: ${theme.colors.grey};
`;

// Closes the page under whichever tab is open. Every dataset here — champions, driver and team
// boards, track records — is built from LEAGUE_SEASON rows only, so the caveat is the same on all
// three tabs and is stated once at the end rather than repeated per section.
export const PageNote = styled.footer`
  margin-top: 56px;
  padding-top: 16px;
  border-top: 2px solid ${theme.colors.secondaryDark};
  max-width: 720px;
  font-size: 12px;
  line-height: 1.6;
  color: ${theme.colors.grey};
`;

export const Grid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(${({ $min }) => $min || "320px"}, 1fr));
  gap: ${({ $gap }) => $gap || "16px"};
`;

export const Card = styled.div`
  background: ${theme.colors.darkLight};
  border: 2px solid ${theme.colors.secondaryDark};
`;

// Every MUI <Skeleton> on this page shares one fill. secondaryDark is the existing border/hover
// colour, so the bars read on both the page background (dark) and the card fill (darkLight) —
// the grey.900 used by components/Driver/StatsTablesSkeleton sits too close to darkLight here.
export const skeletonSx = { bgcolor: theme.colors.secondaryDark };

// Fixed (not random) so re-renders during load don't reshuffle the bars mid-animation.
export const SKELETON_WIDTHS = [120, 96, 140, 88, 132, 104, 156, 92, 124, 112];
export const skeletonWidth = (index) => SKELETON_WIDTHS[index % SKELETON_WIDTHS.length];

export const teamDotStyle = css`
  width: 3px;
  align-self: stretch;
  background: ${({ $color }) => $color || theme.colors.grey};
  flex: none;
`;

export const TeamDot = styled.span`
  ${teamDotStyle}
`;

// TEAM_* board rows carry a team's own id/name/color in holderId/holderName/teamName — there is
// no separate team-of-the-team to show, and no team drill-down panel to open on click.
export function isTeamKind(kind) {
  return typeof kind === "string" && kind.startsWith("TEAM_");
}

export function positionColor(position) {
  if (position === 1) return theme.colors.gold;
  if (position === 2) return theme.colors.silver;
  if (position === 3) return theme.colors.bronze;
  return theme.colors.grey;
}

export const PositionCell = styled.span`
  font-family: "Roboto Condensed", sans-serif;
  font-size: 13px;
  font-weight: 700;
  color: ${({ $position }) => positionColor($position)};
  text-align: center;
`;

export const Ellipsis = styled.span`
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

export const modalOverlaySx = {
  "& .MuiBackdrop-root": {
    backgroundColor: "#000000cc",
    backdropFilter: "blur(4px)",
  },
};

export const modalPaperSx = {
  background: theme.colors.darkLight,
  border: `2px solid ${theme.colors.secondaryDark}`,
  backgroundImage: "none",
  // MUI's Dialog paper is portalled out of PageWrap, so it falls back to the MUI theme's Roboto
  // instead of inheriting the page font — restate it here.
  fontFamily: '"Montserrat", sans-serif',
};

export const CloseButton = styled.button`
  margin-left: auto;
  background: transparent;
  border: 2px solid ${theme.colors.secondaryDark};
  color: ${theme.colors.white};
  width: 32px;
  height: 32px;
  cursor: pointer;
  font-size: 15px;
  flex: none;
  transition: all 0.2s;

  &:hover {
    border-color: ${theme.colors.green};
    color: ${theme.colors.green};
  }
`;

export const ModalHead = styled.div`
  padding: 16px 18px;
  border-bottom: 2px solid ${theme.colors.secondaryDark};
  display: flex;
  align-items: center;
  gap: 12px;
`;

export const ModalHeadText = styled.div`
  display: flex;
  flex-direction: column;
  gap: 3px;
  min-width: 0;
`;

export const ModalTitle = styled.span`
  font-size: 17px;
  font-weight: 700;
`;

export const ModalNote = styled.span`
  font-size: 11px;
  color: ${theme.colors.grey};
`;

export const Pill = styled.button`
  background: ${({ $active }) => ($active ? theme.colors.green : "transparent")};
  color: ${({ $active }) => ($active ? theme.colors.dark : theme.colors.white)};
  border: 2px solid ${({ $active }) => ($active ? theme.colors.green : theme.colors.secondaryDark)};
  padding: 6px 12px;
  font-size: 12px;
  font-family: "Montserrat", sans-serif;
  font-weight: 700;
  letter-spacing: 0.5px;
  cursor: pointer;
`;

export const Divider = styled.div`
  width: 1px;
  height: 24px;
  background: ${theme.colors.secondaryDark};
`;
